import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Check,
    Copy,
    Crown,
    Download,
    Edit3,
    Eye,
    Headphones,
    History,
    KeyRound,
    Plus,
    Search,
    ShieldCheck,
    Trash2,
    UserCheck,
    UserCog,
    UserPlus,
    Users,
    X,
} from 'lucide-react';
import './RolePermissionManagement.css';
import axiosInstance from '../api/AxiosInstance';
import { endpoints } from '../services/endpoints';
import { showErrorToast, showSuccessToast } from '../helpers/SweetAlert';

const roleIcons = {
    'Super Admin': Crown,
    Admin: UserCheck,
    Moderator: ShieldCheck,
    Support: Headphones,
};

const RolePermissionManagement = () => {
    const [roles, setRoles] = useState([]);
    const [permissionGroups, setPermissionGroups] = useState([]);
    const [statistics, setStatistics] = useState({ totalPermissions: 0, activeRoles: 0, usersWithRoles: 0 });
    const [selectedRole, setSelectedRole] = useState('');
    const [search, setSearch] = useState('');
    const [statusFilter, setStatusFilter] = useState('All Roles');
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [newRole, setNewRole] = useState({ name: '', description: '' });
    const [recentChanges, setRecentChanges] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('roles');
    const [systemUsers, setSystemUsers] = useState([]);
    const [systemUserSearch, setSystemUserSearch] = useState('');
    const [showSystemUserModal, setShowSystemUserModal] = useState(false);
    const [editingSystemUser, setEditingSystemUser] = useState(null);
    const [systemUserForm, setSystemUserForm] = useState({ username: '', email: '', password: '', roleId: '' });

    const filteredRoles = useMemo(() => roles.filter((role) => {
        const matchesSearch = `${role.name} ${role.description}`.toLowerCase().includes(search.toLowerCase());
        const matchesStatus = statusFilter === 'All Roles' || role.status === statusFilter.toLowerCase();
        return matchesSearch && matchesStatus;
    }), [roles, search, statusFilter]);

    const activeRole = roles.find((role) => role.name === selectedRole);
    const filteredSystemUsers = useMemo(() => systemUsers.filter((user) => (
        `${user.username} ${user.email} ${user.role?.name || ''}`.toLowerCase().includes(systemUserSearch.toLowerCase())
    )), [systemUsers, systemUserSearch]);

    const loadRoles = useCallback(async (quiet = false) => {
        if (!quiet) setLoading(true);
        try {
            const response = await axiosInstance.get(endpoints.roles);
            const data = response.data.data;
            setRoles(data.roles);
            setPermissionGroups(data.permissionCatalog);
            setStatistics(data.statistics);
            setRecentChanges(data.recentChanges);
            setSelectedRole((current) => data.roles.some((role) => role.name === current)
                ? current
                : (data.roles.find((role) => role.name === 'Admin') || data.roles[0])?.name || '');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to load roles');
        } finally {
            if (!quiet) setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadRoles();
    }, [loadRoles]);

    const loadSystemUsers = useCallback(async () => {
        try {
            const response = await axiosInstance.get(endpoints.systemUsers);
            setSystemUsers(response.data.data);
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to load system users');
        }
    }, []);

    useEffect(() => {
        if (activeTab === 'system-users') loadSystemUsers();
    }, [activeTab, loadSystemUsers]);

    const togglePermission = async (permission) => {
        if (selectedRole === 'Super Admin') return;
        const enabled = activeRole?.permissions.includes(permission);
        const nextPermissions = enabled
            ? activeRole.permissions.filter((item) => item !== permission)
            : [...activeRole.permissions, permission];
        try {
            await axiosInstance.put(`${endpoints.roles}/${activeRole.id}/permissions`, { permissions: nextPermissions });
            await loadRoles(true);
            showSuccessToast('Permissions updated successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to update permissions');
        }
    };

    const createRole = async (event) => {
        event.preventDefault();
        const name = newRole.name.trim();
        if (!name) return;
        try {
            await axiosInstance.post(endpoints.roles, { name, description: newRole.description });
            setNewRole({ name: '', description: '' });
            setShowCreateModal(false);
            setSelectedRole(name);
            await loadRoles(true);
            showSuccessToast('Role created successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to create role');
        }
    };

    const cloneRole = async () => {
        if (!activeRole) return;
        try {
            const response = await axiosInstance.post(`${endpoints.roles}/${activeRole.id}/clone`);
            setSelectedRole(response.data.data.name);
            await loadRoles(true);
            showSuccessToast('Role cloned successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to clone role');
        }
    };

    const deleteRole = async (role) => {
        if (role.isSystem || !window.confirm(`Delete the ${role.name} role?`)) return;
        try {
            await axiosInstance.delete(`${endpoints.roles}/${role.id}`);
            await loadRoles(true);
            showSuccessToast('Role deleted successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to delete role');
        }
    };

    const assignRole = async () => {
        if (!activeRole) return;
        const email = window.prompt(`Enter the verified admin email to assign ${activeRole.name}:`);
        if (!email) return;
        try {
            await axiosInstance.post(endpoints.assignRole, { email, roleId: activeRole.id });
            await loadRoles(true);
            showSuccessToast('Role assigned successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to assign role');
        }
    };

    const exportRoles = () => {
        const rows = [
            ['Role Name', 'Description', 'Users', 'Permissions', 'Status', 'Created'],
            ...roles.map((role) => [role.name, role.description, role.users, role.permissionCount, role.status, role.createdAt]),
        ];
        const csv = rows.map((row) => row.map((value) => `"${String(value).replaceAll('"', '""')}"`).join(',')).join('\n');
        const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }));
        const link = document.createElement('a');
        link.href = url;
        link.download = 'roles.csv';
        link.click();
        URL.revokeObjectURL(url);
    };

    const openCreateSystemUser = () => {
        setEditingSystemUser(null);
        setSystemUserForm({ username: '', email: '', password: '', roleId: roles.find((role) => role.name === 'Admin')?.id || roles[0]?.id || '' });
        setShowSystemUserModal(true);
    };

    const openEditSystemUser = (user) => {
        setEditingSystemUser(user);
        setSystemUserForm({ username: user.username, email: user.email, password: '', roleId: user.role?.id || '' });
        setShowSystemUserModal(true);
    };

    const saveSystemUser = async (event) => {
        event.preventDefault();
        try {
            if (editingSystemUser) {
                await axiosInstance.patch(`${endpoints.systemUsers}/${editingSystemUser.id}`, {
                    username: systemUserForm.username,
                    password: systemUserForm.password || undefined,
                    roleId: systemUserForm.roleId,
                });
            } else {
                await axiosInstance.post(endpoints.systemUsers, systemUserForm);
            }
            setShowSystemUserModal(false);
            await Promise.all([loadSystemUsers(), loadRoles(true)]);
            showSuccessToast(editingSystemUser ? 'System user updated successfully' : 'System user created successfully');
        } catch (error) {
            showErrorToast(error.response?.data?.message || 'Unable to save system user');
        }
    };

    const toggleSystemUserStatus = async (user) => {
        const nextStatus = user.status === 'active' ? 'banned' : 'active';
        const action = nextStatus === 'active' ? 'activate' : 'deactivate';
        if (!window.confirm(`Are you sure you want to ${action} ${user.email}?`)) return;
        try {
            await axiosInstance.patch(`${endpoints.systemUsers}/${user.id}/status`, { status: nextStatus });
            await loadSystemUsers();
            showSuccessToast(`System user ${action}d successfully`);
        } catch (error) {
            showErrorToast(error.response?.data?.message || `Unable to ${action} system user`);
        }
    };

    if (loading) return <div className="role-page role-page-loading">Loading roles...</div>;

    return (
        <section className="role-page">
            <div className="role-page-heading">
                <div>
                    <h1>Role &amp; Permission Management</h1>
                    <p>Create and manage user roles with specific permissions across the platform.</p>
                </div>
                <div className="role-heading-actions">
                    {activeTab === 'roles' ? (
                        <>
                            <button className="role-btn role-btn-secondary" onClick={exportRoles}><Download /> Export Roles</button>
                            <button className="role-btn role-btn-primary" onClick={() => setShowCreateModal(true)}><Plus /> Create Role</button>
                        </>
                    ) : (
                        <button className="role-btn role-btn-primary" onClick={openCreateSystemUser}><UserPlus /> Create System User</button>
                    )}
                </div>
            </div>

            <div className="role-tabs">
                <button className={activeTab === 'roles' ? 'active' : ''} onClick={() => setActiveTab('roles')}><ShieldCheck /> Roles &amp; Permissions</button>
                <button className={activeTab === 'system-users' ? 'active' : ''} onClick={() => setActiveTab('system-users')}><UserCog /> System Users</button>
            </div>

            {activeTab === 'roles' ? <>
            <div className="role-summary-grid">
                <article className="role-card">
                    <h2>Role Overview</h2>
                    <div className="role-overview-list">
                        {roles.slice(0, 4).map((role) => {
                            const Icon = roleIcons[role.name] || ShieldCheck;
                            return (
                                <button key={role.id} className={selectedRole === role.name ? 'selected' : ''} onClick={() => setSelectedRole(role.name)}>
                                    <span><Icon />{role.name}</span>
                                    <span>{role.users} users</span>
                                </button>
                            );
                        })}
                    </div>
                </article>

                <article className="role-card">
                    <h2>Quick Actions</h2>
                    <div className="role-quick-actions">
                        <button onClick={assignRole}><UserPlus /> Assign Role to User</button>
                        <button onClick={() => document.getElementById('permission-matrix')?.scrollIntoView({ behavior: 'smooth' })}><KeyRound /> Manage Permissions</button>
                        <button onClick={() => document.getElementById('role-history')?.scrollIntoView({ behavior: 'smooth' })}><History /> View Role History</button>
                        <button onClick={cloneRole}><Copy /> Clone Role</button>
                    </div>
                </article>

                <article className="role-card role-statistics">
                    <h2>Permission Statistics</h2>
                    <dl>
                        <div><dt>Total Permissions</dt><dd>{statistics.totalPermissions}</dd></div>
                        <div><dt>Active Roles</dt><dd>{statistics.activeRoles}</dd></div>
                        <div><dt>Users with Roles</dt><dd>{statistics.usersWithRoles}</dd></div>
                        <div><dt>Last Modified</dt><dd>{recentChanges.length ? 'Just now' : '2 hours ago'}</dd></div>
                    </dl>
                </article>
            </div>

            <article className="role-card role-table-card">
                <div className="role-table-toolbar">
                    <h2>Roles &amp; Permissions</h2>
                    <div className="role-table-filters">
                        <label><Search /><input value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Search roles..." /></label>
                        <select value={statusFilter} onChange={(event) => setStatusFilter(event.target.value)}>
                            <option>All Roles</option>
                            <option>Active</option>
                            <option>Inactive</option>
                        </select>
                    </div>
                </div>
                <div className="role-table-scroll">
                    <table className="role-table">
                        <thead>
                            <tr><th>Role Name</th><th>Description</th><th>Users</th><th>Permissions</th><th>Status</th><th>Created</th><th>Actions</th></tr>
                        </thead>
                        <tbody>
                            {filteredRoles.map((role) => {
                                const Icon = roleIcons[role.name] || ShieldCheck;
                                return (
                                    <tr key={role.id}>
                                        <td><span className="role-name"><Icon />{role.name}</span></td>
                                        <td>{role.description}</td>
                                        <td>{role.users}</td>
                                        <td>{role.name === 'Super Admin' ? `All (${statistics.totalPermissions})` : `${role.permissionCount} of ${statistics.totalPermissions}`}</td>
                                        <td><span className="role-status">{role.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                                        <td>{new Date(role.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}</td>
                                        <td>
                                            <div className="role-row-actions">
                                                <button title="View" onClick={() => setSelectedRole(role.name)}><Eye /></button>
                                                <button title="Edit permissions" onClick={() => setSelectedRole(role.name)}><Edit3 /></button>
                                                {!role.isSystem && <button title="Delete" onClick={() => deleteRole(role)}><Trash2 /></button>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
            </article>

            <div className="role-bottom-grid">
                <article className="role-card" id="permission-matrix">
                    <div className="permission-title">
                        <h2>Permission Matrix</h2>
                        <select value={selectedRole} onChange={(event) => setSelectedRole(event.target.value)}>
                            {roles.map((role) => <option key={role.id} value={role.name}>{role.name} Role</option>)}
                        </select>
                    </div>
                    <div className="permission-groups">
                        {permissionGroups.map((group) => (
                            <section key={group.title}>
                                <h3>{group.title}</h3>
                                {group.permissions.map((permission) => {
                                    const enabled = selectedRole === 'Super Admin' || activeRole?.permissions.includes(permission.key);
                                    return (
                                        <label key={permission.key}>
                                            <span>{permission.label}</span>
                                            <input type="checkbox" checked={enabled} disabled={selectedRole === 'Super Admin'} onChange={() => togglePermission(permission.key)} />
                                            <span className="permission-check">{enabled && <Check />}</span>
                                        </label>
                                    );
                                })}
                            </section>
                        ))}
                    </div>
                </article>

                <article className="role-card role-assignment" id="role-history">
                    <h2>User Role Assignment</h2>
                    <div className="role-changes">
                        <div className="role-changes-title"><span>Recent Role Changes</span><button>View All</button></div>
                        {recentChanges.length === 0
                            ? <div className="role-empty"><Users /><span>No recent role changes</span></div>
                            : recentChanges.map((change) => <div className="role-change" key={change.id}><span>{change.message}</span><small>{new Date(change.createdAt).toLocaleString()}</small></div>)}
                    </div>
                </article>
            </div>
            </> : (
                <article className="role-card role-table-card system-users-card">
                    <div className="role-table-toolbar">
                        <div>
                            <h2>System Users</h2>
                            <p>Manage admin accounts and assign their access roles.</p>
                        </div>
                        <div className="role-table-filters">
                            <label><Search /><input value={systemUserSearch} onChange={(event) => setSystemUserSearch(event.target.value)} placeholder="Search system users..." /></label>
                        </div>
                    </div>
                    <div className="role-table-scroll">
                        <table className="role-table">
                            <thead><tr><th>System User</th><th>Email</th><th>Assigned Role</th><th>Status</th><th>Created</th><th>Actions</th></tr></thead>
                            <tbody>
                                {filteredSystemUsers.map((user) => (
                                    <tr key={user.id}>
                                        <td><span className="role-name"><UserCog />{user.username || 'Unnamed Admin'}</span></td>
                                        <td>{user.email}</td>
                                        <td><span className="system-role-badge">{user.role?.name || 'Unassigned'}</span></td>
                                        <td><span className={`role-status ${user.status !== 'active' ? 'inactive' : ''}`}>{user.status === 'active' ? 'Active' : 'Inactive'}</span></td>
                                        <td>{new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' })}</td>
                                        <td>
                                            <div className="system-user-actions">
                                                <button className="role-btn role-btn-secondary" onClick={() => openEditSystemUser(user)}><Edit3 /> Edit</button>
                                                <button className={`role-btn ${user.status === 'active' ? 'role-btn-danger' : 'role-btn-secondary'}`} onClick={() => toggleSystemUserStatus(user)}>
                                                    {user.status === 'active' ? 'Deactivate' : 'Activate'}
                                                </button>
                                            </div>
                                        </td>
                                    </tr>
                                ))}
                                {filteredSystemUsers.length === 0 && <tr><td colSpan="6" className="system-users-empty">No system users found.</td></tr>}
                            </tbody>
                        </table>
                    </div>
                </article>
            )}

            {showCreateModal && (
                <div className="role-modal-backdrop" onMouseDown={() => setShowCreateModal(false)}>
                    <form className="role-modal" onSubmit={createRole} onMouseDown={(event) => event.stopPropagation()}>
                        <div className="role-modal-title"><h2>Create Role</h2><button type="button" onClick={() => setShowCreateModal(false)}><X /></button></div>
                        <label>Role Name<input autoFocus value={newRole.name} onChange={(event) => setNewRole({ ...newRole, name: event.target.value })} placeholder="Enter role name" /></label>
                        <label>Description<textarea value={newRole.description} onChange={(event) => setNewRole({ ...newRole, description: event.target.value })} placeholder="Describe this role" /></label>
                        <div className="role-modal-actions">
                            <button type="button" className="role-btn role-btn-secondary" onClick={() => setShowCreateModal(false)}>Cancel</button>
                            <button className="role-btn role-btn-primary"><Plus /> Create Role</button>
                        </div>
                    </form>
                </div>
            )}

            {showSystemUserModal && (
                <div className="role-modal-backdrop" onMouseDown={() => setShowSystemUserModal(false)}>
                    <form className="role-modal" onSubmit={saveSystemUser} onMouseDown={(event) => event.stopPropagation()}>
                        <div className="role-modal-title">
                            <h2>{editingSystemUser ? 'Edit System User' : 'Create System User'}</h2>
                            <button type="button" onClick={() => setShowSystemUserModal(false)}><X /></button>
                        </div>
                        <label>Username<input required value={systemUserForm.username} onChange={(event) => setSystemUserForm({ ...systemUserForm, username: event.target.value })} placeholder="Enter username" /></label>
                        <label>Email<input required type="email" disabled={Boolean(editingSystemUser)} value={systemUserForm.email} onChange={(event) => setSystemUserForm({ ...systemUserForm, email: event.target.value })} placeholder="admin@example.com" /></label>
                        <label>{editingSystemUser ? 'New Password (optional)' : 'Password'}<input required={!editingSystemUser} minLength="8" type="password" value={systemUserForm.password} onChange={(event) => setSystemUserForm({ ...systemUserForm, password: event.target.value })} placeholder="Minimum 8 characters" /></label>
                        <label>Assigned Role
                            <select required value={systemUserForm.roleId} onChange={(event) => setSystemUserForm({ ...systemUserForm, roleId: event.target.value })}>
                                <option value="">Select role</option>
                                {roles.filter((role) => role.status === 'active').map((role) => <option key={role.id} value={role.id}>{role.name}</option>)}
                            </select>
                        </label>
                        <div className="role-modal-actions">
                            <button type="button" className="role-btn role-btn-secondary" onClick={() => setShowSystemUserModal(false)}>Cancel</button>
                            <button className="role-btn role-btn-primary"><UserPlus /> {editingSystemUser ? 'Save Changes' : 'Create User'}</button>
                        </div>
                    </form>
                </div>
            )}
        </section>
    );
};

export default RolePermissionManagement;
